/**
 * A versatile card component that supports various styles and sizes.
 * Can be used as a container for content with optional header, footer, and description.
 *
 * @example
 * ```tsx
 * <Card>
 *   <CardHeader>Header</CardHeader>
 *   <CardContent>Content</CardContent>
 *   <CardFooter>Footer</CardFooter>
 * </Card>
 * ```
 */
import { type VariantProps, cva } from 'class-variance-authority';
import React, { createContext, use } from 'react';

import { Text } from '@core/components/Text';
import { cn } from '@core/utils/styles';

const cardVariants = cva('rounded-theme shadow overflow-hidden', {
  variants: {
    variant: {
      default:
        'border border-stroke-light bg-back-light-muted dark:border-stroke-dark dark:bg-back-dark-muted',
      panel: 'bg-back-light dark:bg-back-dark',
      outline:
        'border-2 border-stroke-light bg-back-light dark:border-stroke-dark dark:bg-back-dark',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

interface CardContextValue {
  size?: 'xs' | 'sm' | 'default' | 'lg';
}

const CardContext = createContext<CardContextValue>({ size: 'default' });

/**
 * Base Card component props
 */
interface CardBaseProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * Main Card component props
 */
interface CardProps extends CardBaseProps, VariantProps<typeof cardVariants> {
  size?: 'xs' | 'sm' | 'default' | 'lg';
}

/**
 * Title specific props
 */
interface CardTitleProps extends Omit<CardBaseProps, 'children'> {
  children: React.ReactNode;
  tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const getPaddingBySize = (size: CardContextValue['size']) => {
  switch (size) {
    case 'xs':
      return 'p-2';
    case 'sm':
      return 'p-4';
    case 'lg':
      return 'p-8';
    default:
      return 'p-6';
  }
};

export const Card = ({
  ref,
  className,
  variant,
  size,
  children,
  ...props
}: CardProps & {
  ref?: React.RefObject<HTMLDivElement>;
}) => {
  const cardSize = size ?? 'default';
  return (
    <CardContext.Provider value={{ size: cardSize }}>
      <div ref={ref} className={cn(cardVariants({ variant }), className)} {...props}>
        {children}
      </div>
    </CardContext.Provider>
  );
};

const useCardContext = () => use(CardContext);

export const CardHeader = ({ className, ...props }: CardBaseProps) => {
  const { size } = useCardContext();
  const padding = getPaddingBySize(size);

  return (
    <div
      className={cn(padding, 'border-stroke-light dark:border-stroke-dark border-b', className)}
      {...props}
    />
  );
};

export const CardTitle = ({ children, tag = 'h2', className }: CardTitleProps) => {
  return (
    <Text as={tag} size="xl" weight="semibold" className={className}>
      {children}
    </Text>
  );
};

export const CardDescription = ({ children, className }: CardBaseProps) => {
  return (
    <Text variant="muted" size="sm" className={className}>
      {children}
    </Text>
  );
};

export const CardContent = ({ children, className }: CardBaseProps) => {
  const { size } = useCardContext();
  const padding = getPaddingBySize(size);

  return <div className={cn(padding, className)}>{children}</div>;
};

export const CardFooter = ({ children, className }: CardBaseProps) => {
  const { size } = useCardContext();
  const padding = getPaddingBySize(size);

  return (
    <div className={cn('border-stroke-light dark:border-stroke-dark border-t', padding, className)}>
      {children}
    </div>
  );
};
